import type { JSX } from 'preact';

/**
 * 18 custom card symbols for Memory (suunnitelma.md section 42). Simple, colourful and clearly
 * different in shape and colour so they stay recognisable at small sizes.
 */
const ICONS: (() => JSX.Element)[] = [
  // 0 sun
  () => (
    <g>
      <g stroke="#f7b733" stroke-width="5" stroke-linecap="round">
        {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => (
          <line x1="32" y1="6" x2="32" y2="14" transform={`rotate(${a} 32 32)`} />
        ))}
      </g>
      <circle cx="32" cy="32" r="13" fill="#fcd34d" />
    </g>
  ),
  // 1 moon
  () => <path d="M40 8a24 24 0 1 0 16 38A20 20 0 0 1 40 8z" fill="#c7d2fe" />,
  // 2 star
  () => <path d="M32 6l7.6 15.8 17.4 2.4-12.6 12.2 3 17.2L32 45.4 16.6 53.6l3-17.2L7 24.2l17.4-2.4z" fill="#facc15" />,
  // 3 heart
  () => <path d="M32 54S8 40 8 23a12 12 0 0 1 24-4 12 12 0 0 1 24 4c0 17-24 31-24 31z" fill="#f43f5e" />,
  // 4 leaf
  () => (
    <g>
      <path d="M12 52C12 24 30 10 54 10c0 26-16 42-42 42z" fill="#22c55e" />
      <path d="M14 50L44 20" stroke="#14532d" stroke-width="3" stroke-linecap="round" />
    </g>
  ),
  // 5 drop
  () => <path d="M32 6C24 20 14 30 14 40a18 18 0 0 0 36 0C50 30 40 20 32 6z" fill="#38bdf8" />,
  // 6 flame
  () => (
    <g>
      <path d="M32 6c4 12 16 18 16 32a16 16 0 0 1-32 0c0-9 6-13 8-20 3 5 3 9 6 11 2-8 2-15 2-23z" fill="#fb923c" />
      <path d="M32 34c2 5 7 7 7 13a7 7 0 0 1-14 0c0-5 5-7 7-13z" fill="#fde047" />
    </g>
  ),
  // 7 snowflake
  () => (
    <g stroke="#e0f2fe" stroke-width="4" stroke-linecap="round" fill="none">
      {[0, 60, 120].map((a) => (
        <g transform={`rotate(${a} 32 32)`}>
          <line x1="32" y1="8" x2="32" y2="56" />
          <path d="M26 14l6 6 6-6M26 50l6-6 6 6" />
        </g>
      ))}
    </g>
  ),
  // 8 anchor
  () => (
    <g stroke="#94a3b8" stroke-width="5" stroke-linecap="round" fill="none">
      <circle cx="32" cy="12" r="5" />
      <line x1="32" y1="17" x2="32" y2="54" />
      <line x1="22" y1="26" x2="42" y2="26" />
      <path d="M12 38c2 10 10 16 20 16s18-6 20-16" />
    </g>
  ),
  // 9 key
  () => (
    <g fill="#eab308">
      <circle cx="20" cy="32" r="12" />
      <circle cx="20" cy="32" r="5" fill="#1b2640" />
      <rect x="30" y="29" width="26" height="6" rx="2" />
      <rect x="46" y="34" width="5" height="9" rx="1" />
      <rect x="38" y="34" width="5" height="6" rx="1" />
    </g>
  ),
  // 10 bell
  () => (
    <g fill="#f59e0b">
      <path d="M32 8a4 4 0 0 1 4 4c10 2 14 12 14 22v8l5 6H9l5-6v-8c0-10 4-20 14-22a4 4 0 0 1 4-4z" />
      <circle cx="32" cy="54" r="5" />
    </g>
  ),
  // 11 crown
  () => (
    <g>
      <path d="M8 22l12 12 12-18 12 18 12-12-5 28H13z" fill="#fbbf24" />
      <rect x="13" y="46" width="38" height="6" rx="2" fill="#d97706" />
    </g>
  ),
  // 12 fish
  () => (
    <g>
      <path d="M6 32c8-12 20-16 32-12 6 2 10 6 12 12-2 6-6 10-12 12-12 4-24 0-32-12z" fill="#fb7185" />
      <path d="M48 32l12-10v20z" fill="#f43f5e" />
      <circle cx="20" cy="29" r="3" fill="#1b2640" />
    </g>
  ),
  // 13 mushroom
  () => (
    <g>
      <path d="M8 32a24 20 0 0 1 48 0z" fill="#ef4444" />
      <circle cx="22" cy="22" r="4" fill="#fff" />
      <circle cx="38" cy="18" r="3" fill="#fff" />
      <circle cx="44" cy="27" r="3" fill="#fff" />
      <rect x="24" y="32" width="16" height="22" rx="6" fill="#fde68a" />
    </g>
  ),
  // 14 cherries
  () => (
    <g>
      <path d="M22 40C24 26 30 14 44 8M42 42C42 28 44 16 44 8" stroke="#15803d" stroke-width="3" fill="none" />
      <circle cx="20" cy="44" r="10" fill="#dc2626" />
      <circle cx="42" cy="46" r="10" fill="#b91c1c" />
    </g>
  ),
  // 15 lightning
  () => <path d="M36 4L12 36h16l-4 24 26-34H34z" fill="#a78bfa" />,
  // 16 diamond
  () => (
    <g>
      <path d="M16 10h32l10 14-26 32L6 24z" fill="#22d3ee" />
      <path d="M6 24h52M16 10l8 14 8-14 8 14 8-14M24 24l8 32 8-32" stroke="#0e7490" stroke-width="2" fill="none" />
    </g>
  ),
  // 17 clover
  () => (
    <g fill="#4ade80">
      <circle cx="32" cy="20" r="10" />
      <circle cx="20" cy="32" r="10" />
      <circle cx="44" cy="32" r="10" />
      <circle cx="32" cy="36" r="8" />
      <path d="M32 40c0 8 2 14 8 18" stroke="#166534" stroke-width="4" fill="none" stroke-linecap="round" />
    </g>
  ),
];

export function MemoryIcon(props: { symbol: number }) {
  const Icon = ICONS[props.symbol % ICONS.length]!;
  return (
    <svg viewBox="0 0 64 64" class="memory-icon" aria-hidden="true">
      <Icon />
    </svg>
  );
}
